(() => {
  const ROOT_SELECTOR = '[data-bxgy-root]';
  const CELEBRATION_SELECTOR = '.hdt-bxgy-celebration';

  let syncInProgress = false;
  let syncTimer = null;
  let celebrationTimer = null;
  let stateInitialized = false;
  let lastCompleteState = false;
  let observer = null;

  const getRoot = () => document.querySelector(ROOT_SELECTOR);

  const isComplete = (root) =>
    String(root?.dataset.bxgyComplete || '').toLowerCase() === 'true';

  const celebrationEnabled = (root) =>
    String(root?.dataset.bxgyCelebrationEnabled || 'true').toLowerCase() !== 'false';

  function removeOldCelebrations() {
    document.querySelectorAll(CELEBRATION_SELECTOR).forEach((el) => {
      if (typeof el.hidePopover === 'function') {
        try {
          el.hidePopover();
        } catch (error) {}
      }
      el.remove();
    });
  }

  function createConfettiPiece() {
    const piece = document.createElement('span');
    const colors = [
      '#24b1c7',
      '#3f8f4b',
      '#f4b740',
      '#eb001b',
      '#7bd88f',
      '#ff8a3d',
      '#8d7cff',
      '#ffffff',
      '#ffd166',
      '#f78fb3'
    ];

    const size = 5 + Math.random() * 9;
    const startX = Math.random() * 100;
    const startY = -40 - Math.random() * 260;
    const drift =
      (Math.random() > 0.5 ? 1 : -1) * (45 + Math.random() * 180);
    const rotate = Math.random() * 360;
    const duration = 4.8 + Math.random() * 1.9;
    const delay = Math.random() * 0.9;
    const opacity = 0.75 + Math.random() * 0.25;

    piece.className = 'hdt-bxgy-confetti-piece';
    piece.style.left = `${startX}vw`;
    piece.style.top = `${startY}px`;
    piece.style.width = `${size}px`;
    piece.style.height = `${size * (0.65 + Math.random() * 0.8)}px`;
    piece.style.backgroundColor =
      colors[Math.floor(Math.random() * colors.length)];
    piece.style.animationDuration = `${duration}s`;
    piece.style.animationDelay = `${delay}s`;
    piece.style.opacity = opacity;
    piece.style.transform = `rotate(${rotate}deg)`;
    piece.style.setProperty('--hdt-confetti-drift', `${drift}px`);
    piece.style.setProperty(
      '--hdt-confetti-swing',
      `${(Math.random() > 0.5 ? 1 : -1) * (18 + Math.random() * 45)}px`
    );
    piece.style.setProperty(
      '--hdt-confetti-rotate',
      `${540 + Math.random() * 900}deg`
    );

    return piece;
  }

  function animateGiftOpening(root) {
    if (!root) return;

    root.classList.remove('is-gift-opening');
    void root.offsetWidth;
    root.classList.add('is-gift-opening');

    window.setTimeout(() => {
      root.classList.remove('is-gift-opening');
    }, 1300);
  }

  function fireCelebration(root) {
    if (!root) return;

    animateGiftOpening(root);

    if (!celebrationEnabled(root)) return;

    clearTimeout(celebrationTimer);
    removeOldCelebrations();

    const layer = document.createElement('div');
    layer.className = 'hdt-bxgy-celebration';
    layer.setAttribute('aria-hidden', 'false');

    const confettiHolder = document.createElement('div');
    confettiHolder.className = 'hdt-bxgy-confetti';
    confettiHolder.setAttribute('aria-hidden', 'true');

    const message = document.createElement('div');
    message.className = 'hdt-bxgy-celebration-message';
    message.setAttribute('role', 'status');
    message.setAttribute('aria-live', 'polite');
    message.textContent =
      root.dataset.bxgyCelebrationMessage || 'مبروك! هديتك بقت معاك 🎁';

    for (let i = 0; i < 170; i += 1) {
      confettiHolder.appendChild(createConfettiPiece());
    }

    layer.appendChild(confettiHolder);
    layer.appendChild(message);

    layer.setAttribute('popover', 'manual');
    document.body.appendChild(layer);

    if (typeof layer.showPopover === 'function') {
      try {
        layer.showPopover();
      } catch (error) {
        document.body.appendChild(layer);
      }
    }

    window.requestAnimationFrame(() => {
      layer.classList.add('is-active');
    });

    celebrationTimer = window.setTimeout(() => {
      layer.classList.remove('is-active');

      window.setTimeout(() => {
        if (typeof layer.hidePopover === 'function') {
          try {
            layer.hidePopover();
          } catch (error) {}
        }

        layer.remove();
      }, 500);
    }, 5600);
  }

  function checkUnlockTransition({ allowCelebration = true } = {}) {
    const root = getRoot();
    if (!root) return;

    const completeNow = isComplete(root);

    if (!stateInitialized) {
      lastCompleteState = completeNow;
      stateInitialized = true;
      return;
    }

    if (completeNow && !lastCompleteState && allowCelebration) {
      fireCelebration(root);
    }

    if (!completeNow) {
      lastCompleteState = false;
      return;
    }

    lastCompleteState = true;
  }

  function replaceOfferFromSectionHTML(sectionHTML, { allowCelebration = true } = {}) {
    const liveRoot = getRoot();
    if (!liveRoot || !sectionHTML) return false;

    const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const freshRoot = doc.querySelector(ROOT_SELECTOR);

    if (!freshRoot) return false;

    liveRoot.replaceWith(freshRoot);

    window.requestAnimationFrame(() => {
      checkUnlockTransition({ allowCelebration });
    });

    return true;
  }

  function syncFromCartUpdateEvent(event) {
    const root = getRoot();
    if (!root) return false;

    const sectionId = root.dataset.sectionId || '';
    const sections = event?.detail?.cartData?.sections;

    if (!sectionId || !sections || !sections[sectionId]) {
      return false;
    }

    /*
      IMPORTANT:
      Kalles already returns the freshly-rendered Cart Drawer section inside
      cart:update. Use that exact HTML instead of firing another request too early.
      This keeps the BXGY bar in sync immediately after + / - / remove actions.
    */
    return replaceOfferFromSectionHTML(sections[sectionId], {
      allowCelebration: true
    });
  }

  async function fetchFreshOfferMarkup(sectionId) {
    const url = new URL(window.location.href);

    url.searchParams.set('sections', sectionId);
    url.searchParams.set('__bxgy', Date.now().toString());

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Could not refresh BXGY section');
    }

    const sections = await response.json();
    return sections?.[sectionId] || '';
  }

  async function syncOfferFromServer({ allowCelebration = true } = {}) {
    const currentRoot = getRoot();

    if (!currentRoot || syncInProgress) return;

    const sectionId = currentRoot.dataset.sectionId || '';
    if (!sectionId) return;

    syncInProgress = true;

    try {
      const sectionHTML = await fetchFreshOfferMarkup(sectionId);

      replaceOfferFromSectionHTML(sectionHTML, {
        allowCelebration
      });
    } catch (error) {
      console.warn('[BXGY] Sync skipped:', error);
    } finally {
      syncInProgress = false;
    }
  }

  function scheduleServerSync(delay = 260, options = {}) {
    window.clearTimeout(syncTimer);

    syncTimer = window.setTimeout(() => {
      syncOfferFromServer(options);
    }, delay);
  }

  /*
    cart:update is the authoritative event:
    it contains the same fresh section HTML Shopify/Kalles uses to redraw the cart.
  */
  document.addEventListener('cart:update', (event) => {
    const synced = syncFromCartUpdateEvent(event);

    // Fallback only if a third-party cart update did not include section HTML.
    if (!synced) {
      scheduleServerSync(300, { allowCelebration: true });
    }
  });

  /*
    No extra fetch is needed on cart:drawer:render. The cart:update event above
    has already supplied the fresh section. Just make sure celebration state is
    aligned with the newly-rendered DOM.
  */
  document.addEventListener('cart:drawer:render', () => {
    window.requestAnimationFrame(() => {
      checkUnlockTransition({ allowCelebration: true });
    });
  });

  /*
    Fallbacks for unusual theme/app mutations that may not dispatch cart:update
    with section HTML.
  */
  document.addEventListener('cart:updated', () =>
    scheduleServerSync(300, { allowCelebration: true })
  );

  document.addEventListener('cart:refresh', () =>
    scheduleServerSync(300, { allowCelebration: true })
  );

  window.addEventListener('pageshow', (event) => {
    checkUnlockTransition({ allowCelebration: false });

    if (event.persisted) {
      scheduleServerSync(80, { allowCelebration: false });
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        checkUnlockTransition({ allowCelebration: false });
      },
      { once: true }
    );
  } else {
    checkUnlockTransition({ allowCelebration: false });
  }

})();
